import { IS_DEV_ENV, PRODUCT_NAMES, PRODUCTS_URLS } from './constants';
import { getSuiteUser, getTenantProductFeatures, getTenantProductConfiguration } from './requests';
import { transformConfigIntoGlobalNavSettings, getAccessibleProductNames, getUserAccessibleProductNames, throwIfNoAccessibleProduct } from './services';
/**
 * @typedef ConfigUrls
 * @property {string?} suiteUserUrl - optional as some products support local login
 * @property {string?} tenantConfigUrl
 * @property {string?} tenantFeaturesUrl
 */

/**
 * @typedef GlobalNavConfig
 * @property {boolean} shouldRenderGlobalNav
 * @property {array?} productsSettings
 * @property {string[]?} userAccessibleProductNames
 * @property {boolean?} shouldShowManagerTabs - THIS PROPERTY IS DEPRECATED AND WILL BE REMOVED IN THE NEXT BREAKING CHANGE!
 * @property {boolean?} shouldShowIdentityAdminTabs - THIS PROPERTY IS DEPRECATED AND WILL BE REMOVED IN THE NEXT BREAKING CHANGE!
 */

/**
 * @typedef SuiteDataError
 * @property {(Error|any)?} tenantFeaturesUrl
 * @property {(Error|any)?} tenantConfigUrl
 * @property {(Error|any)?} suiteUserUrl
 */

/**
 * @typedef PromiseResult
 * @property {string} status
 * @property {any?} value
 * @property {any?} reason
 */

/**
 * @typedef {{claims?: {iss: string, role?: string | string[]}, productManager?: {}, rawUsers?: {}}} SuiteUser
 * @property {{role?: string | string[]}} claims - The claims for the suite user.
 * @property {{}?} productManager - The products that the suite user is a manager in.
 * @property {{}?} rawUsers - The users for the products that the suite user is a user in.
 */

/**
 * process promise results to include errors in an object mapped to their configUrl property name
 * so that consuming apps can identify the error if needed
 * NOTE: heavily depends on the ordering in the `promiseResults` array
 * @param {PromiseResult[]} promiseResults
 * @returns {SuiteDataError}
 */

const errorsFromPromiseResults = promiseResults => {
  // NOTE: promiseResults array promises MUST be in the below order...
  const promiseKeys = ['tenantConfigUrl', 'tenantFeaturesUrl', 'suiteUserUrl'];
  return promiseResults.reduce((acc, {
    reason,
    status
  }, index) => {
    if (status === 'rejected') {
      // map rejections to always be an Error construct if possible/sensible;
      // if the reason was not an error or string, just include it as it was
      const name = promiseKeys[index];
      acc[name] = typeof reason === 'string' || reason === undefined ? new Error(reason) : reason;
    }

    return acc;
  }, {});
};
/**
 * execute all required fetches for data
 * @param {Promise} get
 * @param {ConfigUrls} urls
 * @param {string?} tenantConfigParams
 * @returns {Promise<{
 *   tenantProductFeatures: object,
 *   tenantProductConfig: object,
 *   suiteUser: object,
 *   errors: SuiteDataError,
 * }>}
 */


const getSuiteData = async (get, urls, tenantConfigParams) => {
  const {
    suiteUserUrl,
    tenantConfigUrl,
    tenantFeaturesUrl
  } = urls;
  const configPromise = tenantConfigUrl && getTenantProductConfiguration(get, tenantConfigUrl, tenantConfigParams);
  const featuresPromise = tenantFeaturesUrl && getTenantProductFeatures(get, tenantFeaturesUrl); // allow any url to be excluded in configUrls;
  // just return an empty resolved promise if not provided

  const fetchPromises = [configPromise, featuresPromise].map(promise => promise || Promise.resolve()); // fetch config and features concurrently first

  const promiseResults = await Promise.allSettled(fetchPromises);
  const [tenantProductConfig, tenantProductFeatures] = promiseResults.map(({
    value
  }) => value);
  let suiteUser = null;
  let accessibleProductsError = null;
  let accessibleProducts = getAccessibleProductNames(tenantProductConfig?.config);

  try {
    // check if the products are avaiable based on tenant config.
    throwIfNoAccessibleProduct(accessibleProducts);
    const [suiteUserResult] = await Promise.allSettled([// fetch the user after the config call
    suiteUserUrl ? getSuiteUser(get, suiteUserUrl, accessibleProducts) : Promise.resolve()]);
    suiteUser = suiteUserResult.value; // push the suiteUserResult to the main promise results
    // so that the errors can all be evaluated

    promiseResults.push(suiteUserResult); // check again if the products are accessible based on users.

    if (suiteUserUrl) {
      accessibleProducts = getUserAccessibleProductNames(suiteUser, accessibleProducts);
      throwIfNoAccessibleProduct(accessibleProducts);
    }
  } catch (err) {
    err.accessibleProducts = accessibleProducts;
    accessibleProductsError = err;
  }

  let errors = errorsFromPromiseResults(promiseResults);

  if (accessibleProductsError) {
    errors = { ...errors,
      accessibleProductsError
    };
  }

  return {
    tenantProductConfig,
    tenantProductFeatures,
    suiteUser,
    errors
  };
};
/**
 * process and return config for global nav
 * @param {object} tenantProductConfig
 * @param {object} suiteUser
 * @returns {GlobalNavConfig}
 */


const getGlobalNavConfig = (tenantProductConfig, suiteUser) => {
  // if either the suite user or product config do not exist
  // (from no url being provided, or the promise being rejected)
  // there is no need need to render global nav (or to evaluate product info below)
  if (!suiteUser || !tenantProductConfig) {
    return {
      shouldRenderGlobalNav: false
    };
  } // creates computed props for the access state


  const accessibleProductNames = getAccessibleProductNames(tenantProductConfig?.config);
  const navSettings = transformConfigIntoGlobalNavSettings(tenantProductConfig?.config);
  const userAccessibleProductNames = getUserAccessibleProductNames(suiteUser, accessibleProductNames); // only render Global Nav if settings were returned,
  // and if user will see more than 1 tab.
  // NOTE: not checking against `tenantProductFeatures.features.GlobalNavigation`
  // as this flag is due to be removed.
  // We expect this to be redundant in the future once all tenants have the Suite portal

  const uniqueProductsForUser = new Set(userAccessibleProductNames.map(userAccessibleProductName => PRODUCTS_URLS.find(({
    name
  }) => userAccessibleProductName === name).productName));
  const shouldRenderGlobalNav = navSettings && uniqueProductsForUser.size > 1;
  return {
    productsSettings: navSettings,
    userAccessibleProductNames,
    shouldRenderGlobalNav,
    // TODO: THIS PROPERTY IS DEPRECATED AND WILL BE REMOVED IN THE NEXT BREAKING CHANGE!
    shouldShowManagerTabs: userAccessibleProductNames.includes(PRODUCT_NAMES.TEAM),
    // TODO: THIS PROPERTY IS DEPRECATED AND WILL BE REMOVED IN THE NEXT BREAKING CHANGE!
    shouldShowIdentityAdminTabs: userAccessibleProductNames.includes(PRODUCT_NAMES.IDENTITY_ADMIN)
  };
};
/**
 * get suite context object
 * @param {Promise} get
 * @param {ConfigUrls?} urls
 * @param {string?} additionalTenantConfigParams
 * @returns
 */


const getSuiteContext = async (get, urls, additionalTenantConfigParams) => {
  // example dummy data response in dev mode when no urls are sent
  // so that consuming apps don't have to fake a response from each url
  if (IS_DEV_ENV && !urls) {
    return (await import('./stubbed-data')).default;
  }

  const {
    tenantProductFeatures,
    tenantProductConfig,
    suiteUser,
    errors
  } = await getSuiteData(get, urls, additionalTenantConfigParams); // config for global nav - will only be evaluated
  // if both `tenantProductConfig` and `suiteUser` are defined

  const globalNav = getGlobalNavConfig(tenantProductConfig, suiteUser); // result structure to be included in success and error scenarios

  const result = {
    tenantConfigs: {
      productConfig: tenantProductConfig?.config,
      productFeatures: tenantProductFeatures?.features,
      rawConfigs: {
        productConfig: tenantProductConfig?.raw,
        productFeatures: tenantProductFeatures?.raw
      }
    },
    suiteUser,
    globalNav
  }; // if any of the calls error, reject the promise with all errors included,
  // but include the available data as well

  if (Object.keys(errors).length) {
    const error = new Error('getSuiteContext');
    error.errors = errors;
    error.data = result;
    return Promise.reject(error);
  }

  return result;
};

export default getSuiteContext;
//# sourceMappingURL=getSuiteContext.js.map